import type {
    OrganizationSubscriptionResponse,
    StripeCustomerPortalSessionResponse,
    StripeCustomerSessionResponse
} from 'generatedTypes/user/models';
import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useToast from 'hooks/useToast';

import useTranslation from './hooks/useTranslation';

import {
    CUSTOMER_PORTAL_SESSION_API,
    CUSTOMER_SESSION_API,
    SUBSCRIPTIONS_API,
    TRIAL_DISCOUNT_API
} from './constants';

const useStripe = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { authenticatedGet, authenticatedPost } = useFetch();
    const { broadcastToast } = useToast();
    const {
        APPLYING_TRIAL_DISCOUNT_ERROR_MESSAGE,
        FETCHING_CUSTOMER_PORTAL_ERROR_MESSAGE,
        FETCHING_CUSTOMER_SECRET_ERROR_MESSAGE,
        FETCHING_SUBSCRIPTIONS_ERROR_MESSAGE
    } = useTranslation();

    const fetchCustomerSessionSecret =
        useCallback(async (): Promise<StripeCustomerSessionResponse | null> => {
            try {
                const url = CUSTOMER_SESSION_API;

                const { data } = await authenticatedPost<StripeCustomerSessionResponse | null>(
                    url,
                    null
                );

                return data;
            } catch (error) {
                broadcastToast({
                    text: FETCHING_CUSTOMER_SECRET_ERROR_MESSAGE,
                    type: 'error'
                });
                throw new Error(JSON.stringify(error));
            }
        }, [FETCHING_CUSTOMER_SECRET_ERROR_MESSAGE, authenticatedPost, broadcastToast]);

    const fetchCustomerPortalSessionUrl =
        useCallback(async (): Promise<StripeCustomerPortalSessionResponse | null> => {
            try {
                const url = CUSTOMER_PORTAL_SESSION_API;

                const { data } =
                    await authenticatedPost<StripeCustomerPortalSessionResponse | null>(url, null);

                return data;
            } catch (error) {
                broadcastToast({
                    text: FETCHING_CUSTOMER_PORTAL_ERROR_MESSAGE,
                    type: 'error'
                });
                throw new Error(JSON.stringify(error));
            }
        }, [FETCHING_CUSTOMER_PORTAL_ERROR_MESSAGE, authenticatedPost, broadcastToast]);

    const fetchStripeSubscriptions = useCallback(async (): Promise<
        OrganizationSubscriptionResponse[] | null
    > => {
        setIsLoading(true);

        try {
            const url = SUBSCRIPTIONS_API;

            const { data } = await authenticatedGet<OrganizationSubscriptionResponse[] | null>(
                url,
                null
            );

            return data;
        } catch (error) {
            broadcastToast({
                text: FETCHING_SUBSCRIPTIONS_ERROR_MESSAGE,
                type: 'error'
            });
            throw new Error(JSON.stringify(error));
        } finally {
            setIsLoading(false);
        }
    }, [FETCHING_SUBSCRIPTIONS_ERROR_MESSAGE, authenticatedGet, broadcastToast, setIsLoading]);

    const applyTrialDiscount = useCallback(async (): Promise<void> => {
        try {
            await authenticatedPost(TRIAL_DISCOUNT_API, null);
        } catch (error) {
            broadcastToast({
                text: APPLYING_TRIAL_DISCOUNT_ERROR_MESSAGE,
                type: 'error'
            });
            throw new Error(JSON.stringify(error));
        }
    }, [APPLYING_TRIAL_DISCOUNT_ERROR_MESSAGE, authenticatedPost, broadcastToast]);

    return {
        applyTrialDiscount,
        fetchCustomerPortalSessionUrl,
        fetchCustomerSessionSecret,
        fetchStripeSubscriptions,
        isLoading
    };
};

export default useStripe;
